import React, { useEffect, useState } from 'react';
import { Box } from '@mui/material';
import Grid from '@mui/material/Grid2';
import { UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Arrears, ArrearsStatus } from 'Feature/Arrears/components/ArrearsInquiryPage/core/Arrears';
import { useFlyoutAccess } from 'Feature/Arrears/components/ArrearsInquiryPage/core/useFlyoutAccess';
import { useArrearsUser } from 'Feature/Arrears/components/ArrearsInquiryPage/core/users/useArrearsUser';
import { useUpdateArrears } from 'Feature/Arrears/components/ArrearsInquiryPage/core/useUpdateArrears';
import { ArrearsFlyoutField } from 'Feature/Arrears/components/ArrearsInquiryPage/ui/flyout/fields/ArrearsFlyoutField';
import { ArrearsFlyoutFooter } from 'Feature/Arrears/components/ArrearsInquiryPage/ui/flyout/ArrearsFlyoutFooter';
import { ArrearsFlyoutStatusField } from 'Feature/Arrears/components/ArrearsInquiryPage/ui/flyout/fields/ArrearsFlyoutStatusField';
import { UpdateArrearsModal } from '../UpdateArrearsModal';
import { ArrearsFlyoutAmountField } from 'Feature/Arrears/components/ArrearsInquiryPage/ui/flyout/fields/ArrearsFlyoutAmountField';
import { ArrearsFlyoutDueDateField } from 'Feature/Arrears/components/ArrearsInquiryPage/ui/flyout/fields/ArrearsFlyoutDueDateField';
import { ArrearsFlyoutErrorsBanner } from 'Feature/Arrears/components/ArrearsInquiryPage/ui/flyout/ArrearsFlyoutErrorsBanner';
import { ArrearsFlyoutWarningBanner } from 'Feature/Arrears/components/ArrearsInquiryPage/ui/flyout/ArrearsFlyoutWarningBanner';
import { ArrearsFlyoutInstitutionNumberField } from 'Feature/Arrears/components/ArrearsInquiryPage/ui/flyout/fields/ArrearsFlyoutInstitutionNumberField';
import { ArrearsFlyoutTransitNumberField } from 'Feature/Arrears/components/ArrearsInquiryPage/ui/flyout/fields/ArrearsFlyoutTransitNumberField';
import { ArrearsFlyoutReportingPeriodField } from 'Feature/Arrears/components/ArrearsInquiryPage/ui/flyout/fields/ArrearsFlyoutReportingPeriodField';

export type UpdateArrearsForm = {
  amount?: number;
  dueDate?: string;
  status?: ArrearsStatus;
  institutionNumber?: string;
  transitNumber?: string;
};

export const ArrearsFlyoutContent = ({
  arrears,
  closeFlyout,
  onArrearsUpdated,
  form
}: {
  arrears: Arrears;
  closeFlyout: () => void;
  onArrearsUpdated: (updatedArrears: Arrears) => void;
  form: UseFormReturn<UpdateArrearsForm>;
}) => {
  const { t } = useTranslation('HbtArrearsInquiryPage');

  const { updateArrears } = useUpdateArrears();
  const [isUpdateModalActive, setIsUpdateModalActive] = useState(false);
  const user = useArrearsUser();
  const access = useFlyoutAccess();

  const readOnly = access.isReadOnly(user.roles, arrears);
  const status = form.watch('status');
  const readOnlyOrInactive = readOnly || status === 'inactive';

  useEffect(() => {
    if (status === 'active') {
      form.trigger();
    }
  }, []);

  const handleOnSave = () => {
    if (status === 'inactive') {
      setIsUpdateModalActive(true);
    } else {
      form.handleSubmit(() => setIsUpdateModalActive(true))();
    }
  };

  const handleSubmit = async () => {
    if (status === 'inactive') {
      await form.handleSubmit(submit, submit)();
    } else {
      await form.handleSubmit(submit)();
    }
  };

  const submit = async () => {
    const values = form.getValues();
    const modifiedArrears = {
      ...arrears,
      amount: values.amount!,
      dueDate: values.dueDate!,
      status: values.status!,
      lenderInstitutionNumber: values.institutionNumber!,
      lenderTransitNumber: values.transitNumber!
    };
    const result = await updateArrears(modifiedArrears);
    setIsUpdateModalActive(false);
    if (result.isFailure) {
      handleSubmitError(result.error);
    } else {
      onArrearsUpdated(result.value);
      closeFlyout();
    }
  };

  const handleSubmitError = (errors: string[]) => {
    if (errors.includes('invalid-financial-institution-and-transit-numbers')) {
      form.setError('institutionNumber', {
        message: t('arrearsFlyout.errors.institutionNumber.invalid')
      });
      form.setError('transitNumber', {
        message: t('arrearsFlyout.errors.transitNumber.invalid')
      });
    } else {
      console.log('Update errors: ', errors);
    }
  };

  return (
    <Box display="flex" flexDirection="column" height="100vh">
      <Box display="flex" flexDirection="column" gap="32px" padding="24px 32px">
        <ArrearsFlyoutWarningBanner arrears={arrears} />
        <ArrearsFlyoutErrorsBanner form={form} />

        <ArrearsFlyoutField label={t('arrearsFlyout.loanNumber')} value={arrears.loanNumber} />
        <ArrearsFlyoutStatusField form={form} isReadOnly={readOnly} />

        <Grid container spacing={'32px'}>
          <Grid size={6}>
            <ArrearsFlyoutReportingPeriodField
              label={t('arrearsFlyout.reportingPeriod')}
              arrears={arrears}
            />
          </Grid>
          <Grid size={6}>
            <ArrearsFlyoutDueDateField
              label={t('arrearsFlyout.dueDate')}
              form={form}
              isReadOnly={readOnlyOrInactive}
              arrears={arrears}
            />
          </Grid>
        </Grid>
        <ArrearsFlyoutAmountField
          label={t('arrearsFlyout.amount')}
          isReadOnly={readOnlyOrInactive}
          form={form}
          arrears={arrears}
        />

        <ArrearsFlyoutField label={t('arrearsFlyout.lenderName')} value={arrears.lenderName} />
        <ArrearsFlyoutField label={t('arrearsFlyout.lenderCode')} value={arrears.lenderCode} />

        <Box display="flex" gap="32px" flexDirection="row">
          <ArrearsFlyoutInstitutionNumberField form={form} isReadOnly={readOnlyOrInactive} />
          <ArrearsFlyoutTransitNumberField form={form} isReadOnly={readOnlyOrInactive} />
        </Box>

        <ArrearsFlyoutField
          label={t('arrearsFlyout.transitMunicipality')}
          value={arrears.lenderTransitMunicipality}
        />
      </Box>

      <ArrearsFlyoutFooter closeFlyout={closeFlyout} onSave={handleOnSave} readOnly={readOnly} />

      <UpdateArrearsModal
        isActive={isUpdateModalActive}
        loading={form.formState.isSubmitting}
        onClose={() => setIsUpdateModalActive(false)}
        onConfirm={handleSubmit}
      />
    </Box>
  );
};
